import React from 'react';

type AuthFeature = {
  title: string;
  description: string;
};

type AuthShellProps = {
  headerBadge?: string;
  headerIcon?: React.ReactNode;
  title: string;
  description: string;
  logoUrl?: string | null;
  appName: string;
  heroBadge?: string;
  heroIcon?: React.ReactNode;
  heroTitle?: string;
  heroDescription?: string;
  heroFeatures?: AuthFeature[];
  heroImageUrl?: string;
  heroImageAlt?: string;
  logoPlacement?: 'top-right' | 'inline' | 'none';
  shellVariant?: 'default' | 'flat' | 'split';
  accentClassName?: string;
  children: React.ReactNode;
};

function AuthShell({
  headerBadge,
  headerIcon,
  title,
  description,
  logoUrl,
  appName,
  heroBadge,
  heroIcon,
  heroTitle,
  heroDescription,
  heroFeatures = [],
  heroImageUrl,
  heroImageAlt,
  logoPlacement = 'top-right',
  shellVariant = 'default',
  accentClassName,
  children,
}: AuthShellProps) {
  const accentGlowClass = accentClassName || 'bg-[radial-gradient(circle,_rgba(59,130,246,0.18),_transparent_68%)]';
  const showInlineLogo = logoPlacement === 'inline';
  const showTopRightLogo = logoPlacement === 'top-right' && logoUrl;
  const isSplit = shellVariant === 'split';

  // ── Split layout: full-bleed two-column, no card wrappers ─────────────
  if (isSplit) {
    return (
      <div className="auth-canvas-split">
        <div className="auth-split-stage">
          {heroImageUrl ? (
            <div className="auth-split-image hidden xl:block">
              <img src={heroImageUrl} alt={heroImageAlt || ''} className="h-full w-full object-cover" />
            </div>
          ) : null}
          <div className="auth-split-panel">
            <div className="w-full max-w-[600px]">
              {showInlineLogo ? (
                <div className="auth-brand-lockup mb-12">
                  {logoUrl ? (
                    <img src={logoUrl} alt={appName} className="h-12 w-auto max-w-[16rem] object-contain" />
                  ) : (
                    <div className="text-[1.35rem] font-semibold tracking-[-0.02em] text-on-surface-variant">
                      {appName}
                    </div>
                  )}
                </div>
              ) : null}
              {headerBadge ? (
                <div className="auth-chip mb-4 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-on-surface-variant">
                  {headerIcon}
                  {headerBadge}
                </div>
              ) : null}
              <h2 className="text-[2.4rem] font-semibold tracking-[-0.045em] text-on-surface sm:text-[2.6rem]">
                {title}
              </h2>
              <p className="mt-3 text-[0.9375rem] leading-6 text-on-surface-variant">
                {description}
              </p>
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Default / flat layout ─────────────────────────────────────────────
  const shellClassName = shellVariant === 'flat' ? 'auth-shell auth-shell-flat' : 'auth-shell';
  const mainPanelClassName = shellVariant === 'flat'
    ? 'auth-main-panel auth-main-panel-flat h-full p-5 sm:p-8 xl:flex xl:min-h-full xl:flex-col xl:justify-center'
    : 'auth-main-panel h-full p-5 sm:p-8 xl:flex xl:min-h-full xl:flex-col xl:justify-center';

  return (
    <div className="auth-canvas px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
      <div className="pointer-events-none absolute inset-0">
        <div className={`absolute left-[-8rem] top-[-10rem] h-[24rem] w-[24rem] rounded-full blur-3xl ${accentGlowClass}`} />
        <div className="absolute right-[-9rem] top-[18%] h-[22rem] w-[22rem] rounded-full bg-[radial-gradient(circle,_rgba(96,165,250,0.12),_transparent_72%)] blur-3xl" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-[linear-gradient(180deg,transparent,rgba(10,14,21,0.16))]" />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.9)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.9)_1px,transparent_1px)] [background-size:72px_72px]" />
      </div>

      <div className="auth-stage">
        <div className="grid w-full gap-5 xl:flex xl:gap-8">
          {heroImageUrl ? (
            <section className="auth-hero-panel auth-hero-image-panel hidden xl:flex xl:min-h-[calc(100vh-8rem)] xl:w-[50vw] xl:self-stretch xl:shrink-0">
              <img src={heroImageUrl} alt={heroImageAlt || heroTitle} className="auth-hero-image" />
            </section>
          ) : (
            <section className="auth-hero-panel hidden xl:flex xl:w-[50vw] xl:flex-col xl:justify-center xl:self-stretch xl:shrink-0 p-12">
              <div className="auth-chip px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-300 dark:text-slate-300">
                {heroIcon}
                {heroBadge}
              </div>

              <div className="mt-8 max-w-xl">
                <h1 className="text-5xl font-semibold leading-[1.02] tracking-[-0.05em] text-slate-100 dark:text-slate-50">
                  {heroTitle}
                </h1>
                <p className="mt-5 max-w-lg text-base leading-7 text-slate-300 dark:text-slate-300">
                  {heroDescription}
                </p>
              </div>

              <div className="mt-10 grid gap-4 md:grid-cols-3">
                {heroFeatures.map((feature) => (
                  <div
                    key={feature.title}
                    className="auth-feature-card p-5"
                  >
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-400">
                      {feature.title}
                    </div>
                    <div className="mt-3 text-sm leading-6 text-slate-200 dark:text-slate-200">{feature.description}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className={`${shellClassName} p-2 sm:p-3 lg:p-4 xl:min-h-[calc(100vh-8rem)] xl:min-w-0 xl:flex-1 xl:self-stretch`}>
            <div className={mainPanelClassName}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  {showInlineLogo ? (
                    <div className="auth-brand-lockup">
                      {logoUrl ? (
                        <img src={logoUrl} alt={appName} className="h-11 w-auto max-w-[14rem] object-contain" />
                      ) : (
                        <div className="text-[1.9rem] font-semibold tracking-[-0.04em] text-on-surface">
                          {appName}
                        </div>
                      )}
                    </div>
                  ) : null}
                  {headerBadge ? (
                    <div className="auth-chip px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-on-surface-variant">
                      {headerIcon}
                      {headerBadge}
                    </div>
                  ) : null}
                  <h2 className={`${showInlineLogo ? 'mt-8' : 'mt-4'} text-[2rem] font-semibold tracking-[-0.04em] text-on-surface sm:mt-5 sm:text-[2.2rem]`}>
                    {title}
                  </h2>
                  <p className="mt-2 max-w-md text-sm leading-6 text-on-surface-variant">
                    {description}
                  </p>
                </div>
                {showTopRightLogo ? (
                  <div className="auth-subpanel hidden shrink-0 p-3 sm:flex">
                    <img src={logoUrl} alt={appName} className="h-10 w-auto max-w-[7rem] object-contain" />
                  </div>
                ) : null}
              </div>

              {children}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AuthShell;